<script setup lang="ts">
import { computed } from 'vue'

import type { InsightResult } from '../../models/insight.types'
import {
  isNewResult,
  normalizeSeverity,
  presentInsightTitle,
} from '../../service/insightPresentation'

import { useInsightActionStore } from '@/features/insight-actions'
import { useI18n } from '@/shared/i18n'
import { cur, useFormat } from '@/shared/lib/format'
import { Icon } from '@/shared/ui/icon'
import { PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui/logo'

const props = withDefaults(
  defineProps<{ insight: InsightResult; compact?: boolean; currency?: string | undefined }>(),
  { compact: false },
)
const emit = defineEmits<{ open: [insight: InsightResult] }>()
const { t } = useI18n()
const insightActions = useInsightActionStore()
const activeRun = computed(() => insightActions.runsByInsight[String(props.insight.id)] ?? null)
const format = props.currency ? useFormat({ currency: props.currency }) : useFormat()
const title = computed(
  () =>
    [props.insight.title, props.insight.label, props.insight.config_enum]
      .map(presentInsightTitle)
      .find(Boolean) ?? t('insights.detail.untitled'),
)
const impactMetric = computed(() => presentInsightTitle(props.insight.impact_metric))
const platform = computed(() => ({
  key: props.insight.platform?.trim() || '',
  label: props.insight.platform_label?.trim() || props.insight.platform?.trim() || '',
}))
const platformSrc = computed(() => resolveStaticPlatformLogo(platform.value.key))
const severity = computed(() => normalizeSeverity(props.insight.severity))
const impact = computed(() => {
  const value = Number(props.insight.impact_score)
  if (!Number.isFinite(value)) return null
  if (props.insight.impact_format === 'currency') {
    if (!props.currency) return format.formatCurrency(value, { maxFrac: 0 })
    const amount = format.formatNumber(value, { maxFrac: 0, heavyDefault: true })
    return `${amount}${cur.resolveCurrencySymbol(props.currency, format.locale)}`
  }
  if (props.insight.impact_format === 'percentage') {
    return format.formatPercent(value, { scale: 'percent', maxFrac: 2 })
  }
  return format.formatNumber(value, { maxFrac: 2, heavyDefault: true })
})

function platformLogoProps(): { src: string } | Record<string, never> {
  return platformSrc.value ? { src: platformSrc.value } : {}
}
</script>

<template>
  <button
    type="button"
    class="insight-card grid w-full min-h-27 gap-md p-4 border border-border rounded-md bg-surface text-fg [font:inherit] text-start cursor-pointer transition-[border-color] duration-150 ease-[ease] hover:border-ring hover:outline-none focus-visible:border-ring focus-visible:outline-none"
    :class="[{ 'insight-card--compact': compact }, `insight-card--${severity}`]"
    @click="emit('open', insight)"
  >
    <span class="insight-card__title-row flex items-center gap-sm">
      <PlatformLogo
        v-if="platform.key && platform.key !== 'all'"
        :platform="platform.label"
        v-bind="platformLogoProps()"
        :alt="platform.label"
        :size="18"
        show-text
      />
      <strong class="insight-card__title min-w-0 wrap-anywhere text-[0.9rem] leading-5">{{
        title
      }}</strong>
      <Icon class="insight-card__open flex-none ms-auto text-muted" name="arrow-right" :size="12" />
    </span>
    <span class="insight-card__meta flex items-center flex-wrap gap-sm text-muted text-[0.7rem]">
      <span
        v-if="activeRun"
        class="insight-card__applying p-[0.15rem_0.45rem] rounded-sm text-info bg-info-soft font-semibold"
        data-testid="card-applying"
        >{{
          t('insightActions.footer.applying', { done: activeRun.done, total: activeRun.total })
        }}</span
      >
      <span
        v-if="isNewResult(insight)"
        class="insight-card__new p-[0.15rem_0.45rem] rounded-sm text-info bg-info-soft font-semibold"
        >{{ t('insights.card.new') }}</span
      >
      <span v-if="insight.created_at" class="insight-card__created flex items-center gap-2xs">
        <Icon name="clock" :size="11" />
        {{ format.formatRelative(insight.created_at) }}
      </span>
      <span v-if="impactMetric" class="insight-card__metric">
        {{ impactMetric }}
      </span>
      <strong
        v-if="impact"
        class="insight-card__impact p-[0.15rem_0.45rem] rounded-sm text-success bg-success-soft font-numeric"
        >{{ impact }}</strong
      >
    </span>
  </button>
</template>

<style scoped>
.insight-card.insight-card--compact {
  min-height: auto;
  gap: var(--ui-space-xs);
  padding: var(--ui-space-sm);
}
.insight-card.insight-card--needs_attention .insight-card__impact {
  color: var(--ui-color-danger);
  background-color: var(--ui-color-danger-soft);
}
</style>
